import { S } from "./Squaremap.js";
import { createElement, ArrowLeft, Crosshair, Flame, Globe, House, Minus, Navigation, Plus, Search, Sparkles, Trees, User, Users, X } from "lucide";

/** lucide のアイコン */
function icon(node, size = 16, cls = "sk-svg") {
    const svg = createElement(node);
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("aria-hidden", "true");
    svg.classList.add(cls);
    return svg;
}

/** サイトと同じ魚のロゴ */
function fishLogo(size = 30) {
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", "0 0 100 62");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(Math.round(size * 0.62)));
    svg.setAttribute("aria-hidden", "true");
    svg.classList.add("sk-fish");
    const add = (tag, attrs) => {
        const e = document.createElementNS(ns, tag);
        for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
        svg.appendChild(e);
    };
    add("path", { d: "M24 31 L2 10 Q8 31 2 52 Z", fill: "#ff4655" });
    add("path", { d: "M20 31 C34 6 72 2 96 31 C72 60 34 56 20 31 Z", fill: "#ff4655" });
    add("path", { d: "M58 14 C66 22 66 40 58 48", stroke: "#fff", "stroke-width": "4", "stroke-linecap": "round", fill: "none", opacity: "0.55" });
    add("circle", { cx: "78", cy: "26", r: "5", fill: "#fff" });
    add("circle", { cx: "79", cy: "26", r: "2.4", fill: "#232a3d" });
    return svg;
}

/** ワールドの種類のアイコン */
function worldIcon(type) {
    switch (type) {
        case "nether":
            return Flame;
        case "the_end":
            return Sparkles;
        default:
            return Trees;
    }
}

/**
 * Sakana Network 用のパネル (左上)。
 * 町を探してそこへ飛ぶ・オンラインのプレイヤーを探してそこへ飛ぶ・ワールドの切り替えができる。
 * 町の情報は MapTowny の吹き出し (data-k の付いた所) から読み取る。
 */

const JA = (navigator.language || "ja").toLowerCase().startsWith("ja");
const T = JA
    ? {
          title: "Sakana Network",
          back: "サイトへ",
          search: "町・国・プレイヤーを検索",
          towns: "町",
          players: "プレイヤー",
          worlds: "ワールド",
          noTowns: "町が見つかりません",
          noPlayers: "オンラインのプレイヤーはいません",
          noMatch: "見つかりませんでした",
          nation: "国",
          mayor: "町長",
          residents: "住民",
          size: "広さ",
          chunks: "チャンク",
          founded: "設立",
          people: "人",
          go: "ここへ移動",
          follow: "追いかける",
          following: "追いかけ中",
          close: "閉じる",
          otherWorld: "別のワールド",
          hide: "パネルを閉じる",
          show: "パネルを開く",
          noNation: "国なし",
      }
    : {
          title: "Sakana Network",
          back: "Website",
          search: "Search towns, nations, players",
          towns: "Towns",
          players: "Players",
          worlds: "Worlds",
          noTowns: "No towns yet",
          noPlayers: "No players online",
          noMatch: "No results",
          nation: "Nation",
          mayor: "Mayor",
          residents: "Residents",
          size: "Size",
          chunks: " chunks",
          founded: "Founded",
          people: "",
          go: "Go here",
          follow: "Follow",
          following: "Following",
          close: "Close",
          otherWorld: "Other world",
          hide: "Hide panel",
          show: "Show panel",
          noNation: "No nation",
      };

const SITE_URL = "https://www.sakananw.xyz/" + (JA ? "ja" : "en");

/** HTML の文字だけ */
function htmlText(html) {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    return (doc.body.textContent || "").replace(/[ \t]+/g, " ").replace(/\n\s*\n+/g, "\n").trim();
}

/** 町の吹き出しから町の情報を取り出す */
function parseTown(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const get = (k) => (doc.querySelector(`[data-k="${k}"]`)?.textContent || "").trim();
    const name = get("town");
    if (name) {
        return {
            name,
            nation: get("nation"),
            mayor: get("mayor"),
            residents: get("residents"),
            residentCount: get("residentcount"),
            claims: get("claims"),
            founded: get("founded"),
            board: get("board"),
        };
    }
    // 元のテンプレート: 「町名 (国名)」が最初の行
    if (!doc.querySelector(".infowindow")) return null;
    const first = htmlText(html).split("\n")[0]?.trim();
    if (!first) return null;
    const m = first.match(/^(.+?)\s*\((.*)\)$/);
    return { name: m ? m[1] : first, nation: m ? m[2] : "", mayor: "", residents: "", residentCount: "", claims: "", founded: "", board: "" };
}

function flattenPoints(points) {
    const out = [];
    const walk = (p) => {
        if (Array.isArray(p)) p.forEach(walk);
        else if (p && typeof p === "object" && "x" in p && "z" in p) out.push(p);
    };
    walk(points);
    return out;
}

function markerCenter(m) {
    if (m.point) return m.point;
    if (m.center) return m.center;
    const pts = flattenPoints(m.points);
    if (!pts.length) return null;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const p of pts) {
        minX = Math.min(minX, p.x);
        maxX = Math.max(maxX, p.x);
        minZ = Math.min(minZ, p.z);
        maxZ = Math.max(maxZ, p.z);
    }
    return { x: Math.round((minX + maxX) / 2), z: Math.round((minZ + maxZ) / 2) };
}

function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}

class SakanaPanel {
    constructor() {
        /** @type {Map<string, any>} key = world:town */
        this.towns = new Map();
        this.tab = "towns";
        this.query = "";
        this.selected = null;

        this.root = el("div", "sk-panel");
        this.root.addEventListener("click", (e) => e.stopPropagation());
        this.root.addEventListener("dblclick", (e) => e.stopPropagation());
        this.root.addEventListener("wheel", (e) => e.stopPropagation());

        // 見出し
        const head = el("div", "sk-header");
        const brand = el("div", "sk-brand");
        brand.appendChild(fishLogo(30));
        brand.appendChild(el("span", "sk-title", T.title));
        head.appendChild(brand);
        const back = el("a", "sk-back");
        back.appendChild(icon(ArrowLeft, 14));
        back.appendChild(document.createTextNode(T.back));
        back.href = SITE_URL;
        head.appendChild(back);
        this.toggleBtn = el("button", "sk-toggle");
        this.toggleBtn.appendChild(icon(Minus, 16));
        this.toggleBtn.title = T.hide;
        this.toggleBtn.onclick = () => this.setCollapsed(!this.root.classList.contains("collapsed"));
        head.appendChild(this.toggleBtn);
        this.root.appendChild(head);

        // 本体
        this.body = el("div", "sk-body");

        const searchWrap = el("label", "sk-search-wrap");
        searchWrap.appendChild(icon(Search, 16, "sk-search-icon"));
        this.input = el("input", "sk-search");
        this.input.type = "search";
        this.input.placeholder = T.search;
        this.input.addEventListener("input", () => {
            this.query = this.input.value.trim().toLowerCase();
            this.render();
        });
        searchWrap.appendChild(this.input);
        this.body.appendChild(searchWrap);

        this.tabs = el("div", "sk-tabs");
        for (const [id, label, ic] of [
            ["towns", T.towns, House],
            ["players", T.players, Users],
            ["worlds", T.worlds, Globe],
        ]) {
            const b = el("button", "sk-tab");
            b.appendChild(icon(ic, 15));
            b.appendChild(document.createTextNode(label));
            b.dataset.tab = id;
            b.onclick = () => {
                this.tab = id;
                this.selected = null;
                this.render();
            };
            this.tabs.appendChild(b);
        }
        this.body.appendChild(this.tabs);

        this.detail = el("div", "sk-detail");
        this.body.appendChild(this.detail);

        this.list = el("div", "sk-list");
        this.body.appendChild(this.list);

        this.root.appendChild(this.body);
        document.body.appendChild(this.root);

        // スマホでは最初は閉じておく
        if (window.innerWidth < 640) this.setCollapsed(true);

        this.loadTowns();
        setInterval(() => document.visibilityState === "visible" && this.loadTowns(), 6000);
        setInterval(() => {
            if (document.visibilityState === "visible" && this.tab === "players") this.render();
        }, 2000);
        this.render();
    }

    setCollapsed(collapsed) {
        this.root.classList.toggle("collapsed", collapsed);
        this.toggleBtn.replaceChildren(icon(collapsed ? Plus : Minus, 16));
        this.toggleBtn.title = collapsed ? T.show : T.hide;
    }

    /** すべてのワールドの町を読み込む */
    loadTowns() {
        if (!S.worldList) return;
        const worlds = Array.from(S.worldList.worlds.keys());
        Promise.all(
            worlds.map((w) =>
                fetch(`tiles/${w}/markers.json`, { cache: "no-store" })
                    .then((r) => (r.ok ? r.json() : []))
                    .then((layers) => ({ w, layers }))
                    .catch(() => ({ w, layers: [] })),
            ),
        ).then((results) => {
            const towns = new Map();
            for (const { w, layers } of results) {
                if (!Array.isArray(layers)) continue;
                for (const layer of layers) {
                    for (const m of layer.markers || []) {
                        const info = parseTown(m.popup || m.tooltip);
                        if (!info) continue;
                        const key = w + ":" + info.name.toLowerCase();
                        const center = markerCenter(m);
                        const prev = towns.get(key);
                        if (!prev) {
                            if (center) towns.set(key, { key, world: w, ...info, center });
                        } else if (m.type === "icon" && center) {
                            prev.center = center; // 町の中心 (ホーム) を優先
                        }
                    }
                }
            }
            this.towns = towns;
            this.render();
        });
    }

    /** 指定した場所へ移動する */
    goTo(world, x, z, zoomOffset = 1) {
        const go = () => {
            const w = S.worldList.curWorld;
            const zoom = Math.max(w.zoom.def, w.zoom.max - zoomOffset);
            S.map.flyTo(S.toLatLng(x, z), zoom, { duration: 0.8 });
        };
        if (S.worldList.curWorld?.name !== world) {
            S.playerList.clearPlayerMarkers();
            S.worldList.showWorld(world, go);
        } else {
            go();
        }
        if (window.innerWidth < 640) this.setCollapsed(true);
    }

    worldName(name) {
        return S.worldList.worlds.get(name)?.display_name || name;
    }

    render() {
        for (const b of this.tabs.children) b.classList.toggle("active", b.dataset.tab === this.tab);
        this.list.replaceChildren();
        this.detail.replaceChildren();
        this.detail.style.display = "none";

        if (this.tab === "towns") this.renderTowns();
        else if (this.tab === "players") this.renderPlayers();
        else this.renderWorlds();
    }

    renderTowns() {
        const q = this.query;
        const towns = Array.from(this.towns.values())
            .filter((t) => !q || t.name.toLowerCase().includes(q) || t.nation.toLowerCase().includes(q) || t.mayor.toLowerCase().includes(q))
            .sort((a, b) => a.name.localeCompare(b.name, "ja"));

        if (this.selected && this.towns.has(this.selected)) {
            this.renderTownDetail(this.towns.get(this.selected));
        }
        if (!towns.length) {
            this.list.appendChild(el("div", "sk-empty", this.towns.size ? T.noMatch : T.noTowns));
            return;
        }
        for (const t of towns) {
            const item = el("button", "sk-item" + (this.selected === t.key ? " active" : ""));
            const townIcon = el("span", "sk-icon");
            townIcon.appendChild(icon(House, 17));
            const text = el("span", "sk-text");
            text.appendChild(el("span", "sk-name", t.name));
            const sub = [t.nation || T.noNation];
            if (t.residentCount) sub.push(`${t.residentCount}${T.people}`);
            if (t.claims) sub.push(`${t.claims}${T.chunks}`);
            if (t.world !== S.worldList.curWorld?.name) sub.push(this.worldName(t.world));
            text.appendChild(el("span", "sk-sub", sub.join(" ・ ")));
            item.appendChild(townIcon);
            item.appendChild(text);
            item.onclick = () => {
                this.selected = t.key;
                this.goTo(t.world, t.center.x, t.center.z, 1);
                this.render();
            };
            this.list.appendChild(item);
        }
    }

    renderTownDetail(t) {
        this.detail.style.display = "";
        const card = el("div", "sk-card");
        const top = el("div", "sk-card-top");
        top.appendChild(el("div", "sk-card-title", t.name));
        const close = el("button", "sk-x");
        close.appendChild(icon(X, 15));
        close.title = T.close;
        close.onclick = () => {
            this.selected = null;
            this.render();
        };
        top.appendChild(close);
        card.appendChild(top);

        const rows = [
            [T.nation, t.nation || T.noNation],
            [T.mayor, t.mayor],
            [T.residents, t.residentCount ? `${t.residentCount}${T.people}` : ""],
            [T.size, t.claims ? `${t.claims}${T.chunks}（${(Number(t.claims) * 256).toLocaleString()} ${JA ? "ブロック²" : "blocks²"}）` : ""],
            [T.founded, t.founded],
        ];
        const dl = el("dl", "sk-dl");
        for (const [k, v] of rows) {
            if (!v) continue;
            dl.appendChild(el("dt", null, k));
            dl.appendChild(el("dd", null, v));
        }
        card.appendChild(dl);
        if (t.residents) card.appendChild(el("p", "sk-residents", t.residents));
        if (t.board) card.appendChild(el("p", "sk-board", t.board));
        const go = el("button", "sk-btn");
        go.appendChild(icon(Navigation, 15));
        go.appendChild(document.createTextNode(T.go));
        go.onclick = () => this.goTo(t.world, t.center.x, t.center.z, 0);
        card.appendChild(go);
        this.detail.appendChild(card);
    }

    renderPlayers() {
        const q = this.query;
        const players = Array.from(S.playerList?.players?.values() || [])
            .filter((p) => !q || p.name.toLowerCase().includes(q) || htmlText(p.displayName).toLowerCase().includes(q))
            .sort((a, b) => a.name.localeCompare(b.name));
        if (!players.length) {
            this.list.appendChild(el("div", "sk-empty", S.playerList?.players?.size ? T.noMatch : T.noPlayers));
            return;
        }
        for (const p of players) {
            const following = S.playerList.following === p.uuid;
            const item = el("button", "sk-item" + (following ? " active" : ""));
            const head = el("img", "sk-head");
            try {
                p.constructor.watchHead?.(head);
                head.src = p.getHeadUrl();
            } catch {
                head.src = "images/icon/player.png";
            }
            head.alt = "";
            head.loading = "lazy";
            head.onerror = () => {
                // 顔が取れない (統合版など) ときは人のアイコン
                const ph = el("span", "sk-icon");
                ph.appendChild(icon(User, 17));
                head.replaceWith(ph);
            };
            const text = el("span", "sk-text");
            // 名前は TAB と同じ色付き (⚔ の印・LuckPerms の接頭辞)。地図の名札と同じく、サーバーが作った HTML をそのまま使う
            const nameEl = el("span", "sk-name");
            if (p.displayName && p.displayName !== p.name) nameEl.innerHTML = p.displayName;
            else nameEl.textContent = p.name;
            text.appendChild(nameEl);
            const sub = [];
            if (p.position) sub.push(`X ${Math.round(p.position.x)} / Z ${Math.round(p.position.z)}`);
            sub.push(this.worldName(p.world));
            text.appendChild(el("span", "sk-sub", sub.join(" ・ ")));
            item.appendChild(head);
            item.appendChild(text);
            if (following) {
                const badge = el("span", "sk-badge");
                badge.appendChild(icon(Crosshair, 12));
                badge.appendChild(document.createTextNode(T.following));
                item.appendChild(badge);
            }
            item.onclick = () => {
                if (S.playerList.following === p.uuid) {
                    S.playerList.followPlayerMarker(null);
                } else if (S.playerList.showPlayer(p.uuid)) {
                    S.playerList.followPlayerMarker(p.uuid);
                    if (p.position) this.goTo(p.world, p.position.x, p.position.z, 1);
                }
                this.render();
            };
            this.list.appendChild(item);
        }
    }

    renderWorlds() {
        for (const [name, world] of S.worldList.worlds) {
            const cur = S.worldList.curWorld?.name === name;
            const item = el("button", "sk-item" + (cur ? " active" : ""));
            const wIcon = el("span", "sk-icon sk-world-" + (world.type || "normal"));
            wIcon.appendChild(icon(worldIcon(world.type), 17));
            const text = el("span", "sk-text");
            text.appendChild(el("span", "sk-name", world.display_name));
            item.appendChild(wIcon);
            item.appendChild(text);
            item.onclick = () => {
                if (cur) {
                    S.centerOn(world.spawn.x, world.spawn.z, world.zoom.def);
                } else {
                    S.playerList.clearPlayerMarkers();
                    S.worldList.showWorld(name, () => {
                        const w = S.worldList.curWorld;
                        S.centerOn(w.spawn.x, w.spawn.z, w.zoom.def);
                        this.render();
                    });
                }
            };
            this.list.appendChild(item);
        }
    }
}

export { SakanaPanel };
